import React, { useState } from "react";
import { Box, Button, IconButton, ListItemText, Menu, MenuItem, Tooltip, useMediaQuery } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { accountRequests } from "api/accountApi";
import dataProvider from "server/dataProvider";
import { useNotifyError } from "utils/useNotifyError";
import { applySessionAndLoadUser } from "auth/security/authProvider";
import { getEmployeeProfiles } from "auth/security/securityUtils";

const profileKey = profile => `${profile.nodeId}-${profile.positionId}`;

/**
 * App-bar control for position-based employee profiles: shows the node/position the session is bound to and
 * lets the employee switch to another one (a new session replaces the current one).
 */
const ProfileSwitcher = () => {
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const [anchorEl, setAnchorEl] = useState(null);
  const [switching, setSwitching] = useState(false);

  const cached = getEmployeeProfiles();
  const profiles = cached?.profiles ?? [];

  if (!cached?.enabled || profiles.length <= 1) return null;

  const current = cached.current
    ? profiles.find(p => p.nodeId === cached.current.nodeId && p.positionId === cached.current.positionId)
    : null;
  const others = profiles.filter(p => !current || profileKey(p) !== profileKey(current));
  const label = current ? `${current.nodeName} · ${current.positionName}` : "Изберете профил";

  const handleSwitch = async profile => {
    setAnchorEl(null);
    if (switching) return;
    setSwitching(true);
    try {
      const { data } = await dataProvider.customRequest(
        accountRequests.postSwitchProfile(),
        { nodeId: profile.nodeId, positionId: profile.positionId },
        "POST",
      );
      await applySessionAndLoadUser(data.sessionId);
      // Full reload so <Admin> rebuilds the resources and menu from the new session's permissions.
      window.location.href = "/";
    } catch (error) {
      notifyError(error);
      setSwitching(false);
    }
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
      <Tooltip title={`Профил: ${label}. Натиснете, за да смените профила.`}>
        {/* span: a disabled button cannot anchor a Tooltip (no events, MUI warns) */}
        <span>
          {isMobile ? (
            <IconButton
              color="inherit"
              onClick={e => setAnchorEl(e.currentTarget)}
              disabled={switching}
            >
              <SwapHorizIcon />
            </IconButton>
          ) : (
            <Button
              color="inherit"
              startIcon={<SwapHorizIcon />}
              onClick={e => setAnchorEl(e.currentTarget)}
              disabled={switching}
              sx={{ textTransform: "none", maxWidth: "20rem" }}
            >
              <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</Box>
            </Button>
          )}
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {others.map(profile => (
          <MenuItem
            key={profileKey(profile)}
            onClick={() => handleSwitch(profile)}
          >
            <ListItemText
              primary={profile.nodeName}
              secondary={profile.positionName}
            />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default ProfileSwitcher;
